import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Clock, Award, CheckCircle, FileText, Users, Zap, Building2, Home as HomeIcon, Lock, Truck, Leaf } from 'lucide-react';

function GovernmentContracting() {
  const [activeTab, setActiveTab] = useState('services');

  const services = [
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Rapid Deployment & Emergency Response',
      description: 'When time is critical, Williams Diversified delivers. Our rapid deployment teams are trained and equipped to mobilize nationwide for emergency response, disaster relief, and urgent government projects.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/lorwseaf_image.jpeg'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Base Operations & DoD Support',
      description: 'Supporting our nation\'s military with comprehensive base operations and infrastructure services. We specialize in facility maintenance, construction, and support services for Department of Defense installations worldwide.',
      image: 'https://images.unsplash.com/photo-1708733818543-8a895c354151?fm=webp&q=70&auto=format&fit=max&w=800'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Department of Justice & Federal Facility Support',
      description: 'Providing specialized construction and facility services for Department of Justice facilities and other federal buildings. We understand the unique requirements and compliance standards of federal facilities.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/8h83oyxr_image.jpeg'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Construction & Site Development',
      description: 'Full-scale construction services tailored for government projects. From ground-up construction to major renovations, we deliver projects that meet strict federal standards and specifications.',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?fm=webp&q=70&auto=format&fit=max&w=800'
    },
    {
      icon: <HomeIcon className="w-12 h-12" />,
      title: 'Temporary Housing & Camp Setup',
      description: 'Rapid deployment of temporary housing solutions for military personnel, emergency responders, and government contractors. We design, construct, and maintain temporary living facilities that provide comfort and functionality in any environment.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/l92ff6lk_image.png'
    },
    {
      icon: <Lock className="w-12 h-12" />,
      title: 'Security & Access Control',
      description: 'Installation and maintenance of advanced security systems and access control solutions for government facilities. We understand the critical importance of facility security and provide comprehensive solutions.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/kf5nfdt2_image.png'
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: 'Logistics & Transportation Support',
      description: 'Comprehensive logistics coordination and transportation support for government construction projects. We manage complex supply chains, equipment transport, and material delivery to ensure projects stay on schedule.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/4ssmbikq_image.png'
    },
    {
      icon: <Leaf className="w-12 h-12" />,
      title: 'Environmental & Infrastructure Services',
      description: 'Specialized environmental services and infrastructure development for government projects. From environmental remediation to utilities installation, we provide sustainable solutions that meet federal environmental standards.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/2va1djob_image.jpeg'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Temporary Power',
      description: 'Reliable temporary power solutions for government operations, emergency response, and construction projects. Our temporary power systems ensure uninterrupted operations for critical facilities and emergency situations.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/w3zqf7cy_image.jpeg'
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Defense and Aerospace',
      description: 'Specialized construction and support services for defense and aerospace facilities. We work with defense contractors and aerospace companies to build and maintain facilities that meet stringent security and operational requirements.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/r1n130ok_image.jpeg'
    },
    {
      icon: <HomeIcon className="w-12 h-12" />,
      title: 'Healthcare Facilities (VA & Military Medical)',
      description: 'Construction and facility support for VA hospitals, military medical centers, and government healthcare facilities. We understand the unique requirements of medical environments and deliver projects that meet strict healthcare standards.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/5bqj55e3_image.jpeg'
    },
    {
      icon: <HomeIcon className="w-12 h-12" />,
      title: 'Exterior Services (Roofing, Facades, Site Work)',
      description: 'Comprehensive exterior construction for government facilities including roofing, building envelopes, site work, parking facilities, and exterior improvements meeting federal specifications and durability standards.',
      image: 'https://customer-assets.emergentagent.com/job_diversified-web/artifacts/9onms9n8_image.jpeg'
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Federal Contracting</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Government Contracting <span className="text-gold">Capabilities</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Mission-ready contractor pursuing federal opportunities with proven capabilities, rapid deployment, and unwavering commitment to compliance.
          </p>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="bg-black border-t border-gold/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('services')}
              className={`py-3 px-8 text-base font-semibold uppercase tracking-wide transition-all ${
                activeTab === 'services'
                  ? 'bg-gold text-black border-b-2 border-gold'
                  : 'bg-black text-gray-400 hover:text-gold border-b-2 border-transparent hover:border-gold/50'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('capabilities')}
              className={`py-3 px-8 text-base font-semibold uppercase tracking-wide transition-all ${
                activeTab === 'capabilities'
                  ? 'bg-gold text-black border-b-2 border-gold'
                  : 'bg-black text-gray-400 hover:text-gold border-b-2 border-transparent hover:border-gold/50'
              }`}
            >
              Federal Capabilities
            </button>
            <button
              onClick={() => setActiveTab('naics')}
              className={`py-3 px-8 text-base font-semibold uppercase tracking-wide transition-all ${
                activeTab === 'naics'
                  ? 'bg-gold text-black border-b-2 border-gold'
                  : 'bg-black text-gray-400 hover:text-gold border-b-2 border-transparent hover:border-gold/50'
              }`}
            >
              NAICS Codes
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className={`bg-black pb-12 pt-16 relative overflow-hidden ${activeTab === 'services' ? 'block' : 'hidden'}`}>
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Comprehensive <span className="text-gold">Services</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">
              Complete government contracting solutions across all critical areas
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {services.map((service, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl min-h-[500px]"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${service.image})` }}
                ></div>
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/50 group-hover:from-black/95 group-hover:via-black/75 transition-all"></div>
                
                {/* Content */}
                <div className="relative z-10 p-12 h-full flex flex-col justify-end">
                  <div className="text-gold mb-6 group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed text-lg flex-grow">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Federal Capabilities Tab Content */}
      <div className={`${activeTab === 'capabilities' ? 'block' : 'hidden'}`}>

      {/* Core Capabilities Overview */}
      <section className="bg-black pt-16 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Core <span className="text-gold">Capabilities</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">
              Comprehensive construction and facility services tailored for federal requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Zap className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Rapid Response</h3>
              <p className="text-gray-400">48-hour deployment for emergency repairs, temporary shelters, power generation, and critical disaster response nationwide</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Building2 className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Facility Services</h3>
              <p className="text-gray-400">Complete construction, renovation, and maintenance for government facilities</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Award className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Compliance Ready</h3>
              <p className="text-gray-400">Deep understanding of FAR, DFARS, and federal facility standards</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Users className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Experienced Team</h3>
              <p className="text-gray-400">Skilled workforce ready to meet mission-critical requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-8 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            
            {/* Section 1: Our Commitment */}
            <div className="">
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  1. Commitment to Federal Service
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>
                  Williams Diversified LLC is actively pursuing government contracting opportunities and is committed to becoming a trusted partner for federal agencies nationwide. We bring commercial construction expertise, rapid deployment capabilities, and a deep understanding of the unique requirements that define government work.
                </p>
                <p>
                  Our team is prepared to deliver mission-critical services where precision, accountability, and unwavering reliability are non-negotiable. We understand that government facilities require contractors who can respond immediately, maintain strict compliance standards, and execute flawlessly under pressure.
                </p>
              </div>
            </div>

            {/* Section 2: Service Capabilities */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                2. Service Capabilities
              </h2>
              <div className="space-y-6">
                <div className="bg-black border border-gold/30 rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-gold mb-4">Construction Services</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Ground-up construction and new facilities</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Building renovations and modernization</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Facility additions and expansions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Structural repairs and reinforcement</span>
                      </li>
                    </ul>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Secure facility construction</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Office build-outs and tenant improvements</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Warehouse and industrial facilities</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Training and education facilities</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-black border border-gold/30 rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-gold mb-4">Specialized Services</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Emergency repair and restoration</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>HVAC installation and upgrades</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Electrical systems and infrastructure</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Plumbing and mechanical systems</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Roofing and exterior envelope</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Site preparation and earthwork</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Demolition and environmental services</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Painting and protective coatings</span>
                      </li>
                    </ul>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Temporary & emergency power generation</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Modular shelters & temporary structures deployment</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Tents & life-support structure rental</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Temporary building leasing and setup</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Equipment rental and leasing</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Hazardous waste treatment and disposal</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Environmental remediation services</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Facilities support and maintenance (MRO)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Aircraft ground support services</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Engineering and environmental consulting</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Professional training and development</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-black border border-gold/30 rounded-lg p-6">
                  <h3 className="text-2xl font-bold text-gold mb-4">Project Management</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Comprehensive project planning and scheduling</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Budget management and cost control</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Quality assurance and control</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Safety management and OSHA compliance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Coordination with government representatives</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 3: Federal Agencies We Can Support */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                3. Federal Agencies We Are Ready to Support
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We are prepared to provide construction and facility services to federal agencies including:
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-3">Defense & Security</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Department of Defense (DoD)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Department of Homeland Security (DHS)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Department of Justice (DOJ)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Federal Bureau of Prisons (BOP)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>U.S. Marshals Service</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <h3 className="text-xl font-bold text-white mb-3">Civilian Agencies</h3>
                    <ul className="space-y-2 text-gray-300">
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>General Services Administration (GSA)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Veterans Affairs (VA)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Department of Energy (DOE)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Environmental Protection Agency (EPA)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-gold mr-3">•</span>
                        <span>Federal Emergency Management Agency (FEMA)</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <p className="mt-6">
                  We are also prepared to support state, county, and municipal government projects nationwide.
                </p>
              </div>
            </div>

            {/* Section 4: Compliance & Standards */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                4. Compliance & Federal Standards Readiness
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We maintain thorough knowledge of federal contracting requirements and are prepared to meet all compliance standards:
                </p>
                <div className="bg-black border border-gold/30 rounded-lg p-6 mt-6">
                  <h3 className="text-xl font-bold text-gold mb-4">Federal Acquisition Regulations (FAR)</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Understanding of FAR clauses and compliance requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Cost accounting and financial record-keeping systems</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Buy American Act compliance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Davis-Bacon wage requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Equal opportunity and affirmative action</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-black border border-gold/30 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gold mb-4">Quality & Safety Standards</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>OSHA safety training and compliance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Quality control and quality assurance programs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Environmental compliance (EPA, local regulations)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Building codes and federal facility standards</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-gold mr-3">•</span>
                      <span>Documentation and reporting protocols</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 5: Business Information */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                5. Business Information
              </h2>
              <div className="space-y-4 text-lg">
                <div className="bg-black border border-gold/30 rounded-lg p-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-gold font-bold min-w-[200px]">Legal Business Name:</div>
                      <div className="">Williams Diversified LLC</div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="text-gold font-bold min-w-[200px]">Headquarters:</div>
                      <div className="">Tulsa, Oklahoma</div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="text-gold font-bold min-w-[200px]">Service Area:</div>
                      <div className="">Nationwide and Worldwide</div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="text-gold font-bold min-w-[200px]">Small Business:</div>
                      <div className="">Yes</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      </div>
      {/* End Federal Capabilities Tab */}

      {/* NAICS Codes Tab Content */}
      <div className={`${activeTab === 'naics' ? 'block' : 'hidden'}`}>
        <section className="bg-black pt-16 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 brass-lighting"></div>
          <div className="absolute inset-0 geometric-brass"></div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                <span className="text-gold">NAICS</span> Codes
              </h2>
              <p className="text-gray-300 text-xl max-w-3xl mx-auto">
                Our comprehensive NAICS code coverage for federal contracting opportunities
              </p>
            </div>

            {/* Emergency Response & Disaster Services */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Emergency Response & Disaster Services
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">221118</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Other Electric Power Generation (Temporary & Emergency Power Services)</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">531190</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Leasing/Providing Temporary Buildings & Shelters (BOS & Emergency Contracts)</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">532289</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Renting/Deploying Tents, Modular Shelters, Temporary Life-Support Structures (FEMA/DoD)</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">532490</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Commercial/Industrial Machinery & Equipment Rental & Leasing (Generators & Specialty Equipment)</p>
                </div>
              </div>
            </div>

            {/* Construction & Site Services */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Construction & Site Services
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">238910</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Site Preparation Contractors</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">238990</div>
                  <p className="text-gray-300 text-sm leading-relaxed">All Other Specialty Trade Contractors</p>
                </div>
              </div>
            </div>

            {/* Transportation Support */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Transportation Support
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">488190</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Other Support Activities for Air Transportation (Aircraft Ground Support)</p>
                </div>
              </div>
            </div>

            {/* Consulting Services */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Consulting Services
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">541330</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Engineering Services</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">541611</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Administrative & General Management Consulting Services</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">541614</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Process, Physical Distribution, and Logistics Consulting Services</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">541620</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Environmental Consulting Services</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">541715</div>
                  <p className="text-gray-300 text-sm leading-relaxed">R&D in Physical, Engineering, and Life Sciences</p>
                </div>
              </div>
            </div>

            {/* Facilities & Support Services */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Facilities & Support Services
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">561210</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Facilities Support Services</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">811310</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Commercial & Industrial Machinery & Equipment Repair & Maintenance (MRO Services)</p>
                </div>
              </div>
            </div>

            {/* Environmental Services */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Environmental Services
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">562211</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Hazardous Waste Treatment and Disposal</p>
                </div>
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">562910</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Remediation Services</p>
                </div>
              </div>
            </div>

            {/* Training & Development */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gold mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                Training & Development
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-black to-gray-900 border border-gold/30 rounded-lg p-6 hover:border-gold transition-all">
                  <div className="text-4xl font-bold text-gold mb-3">611430</div>
                  <p className="text-gray-300 text-sm leading-relaxed">Professional and Management Development Training</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* End NAICS Tab */}

      {/* Why Choose Us - Shown on all tabs */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            {/* Section 6: Why Choose Us */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                6. Why Choose Williams Diversified LLC
              </h2>
              <div className="space-y-4 text-lg">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Clock className="w-8 h-8 text-gold" />
                      <h3 className="text-xl font-bold text-white">Rapid Response</h3>
                    </div>
                    <p className="text-gray-300">
                      48-hour deployment capability nationwide. We understand that government facilities cannot afford downtime and we respond immediately to critical needs.
                    </p>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="w-8 h-8 text-gold" />
                      <h3 className="text-xl font-bold text-white">Mission Focus</h3>
                    </div>
                    <p className="text-gray-300">
                      We understand mission-critical operations. Our team approaches every project with the urgency and precision that government work demands.
                    </p>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Award className="w-8 h-8 text-gold" />
                      <h3 className="text-xl font-bold text-white">Quality Commitment</h3>
                    </div>
                    <p className="text-gray-300">
                      We deliver work that meets or exceeds federal standards. Every project is executed with meticulous attention to detail and quality.
                    </p>
                  </div>

                  <div className="bg-black border border-gold/30 rounded-lg p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <FileText className="w-8 h-8 text-gold" />
                      <h3 className="text-xl font-bold text-white">Documentation</h3>
                    </div>
                    <p className="text-gray-300">
                      Comprehensive documentation, reporting, and record-keeping that meet government requirements and audit standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 7: Past Performance */}
            <div className="">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                7. Proven Commercial Performance
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  While we are actively pursuing our first federal contract, we bring extensive commercial construction experience that directly translates to government work:
                </p>
                <ul className="space-y-3 ml-6 mt-4">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">100+ Projects Completed Nationwide</strong> - Proven track record of successful project delivery</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">On-Time, On-Budget Performance</strong> - Consistent delivery meeting schedule and cost targets</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Safety Record</strong> - Commitment to zero-incident work environments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Client Satisfaction</strong> - Long-term relationships built on trust and performance</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-gold/10 to-transparent border-l-4 border-gold rounded-lg p-8 mt-12">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                8. Partner With Us
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We are actively seeking opportunities to serve federal agencies as both prime contractors and subcontractors. Our team is ready to discuss how we can support your agency's mission-critical requirements.
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[180px]">Contracting Officers:</div>
                    <div className="">Contact us for capability briefings and project discussions</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[180px]">Prime Contractors:</div>
                    <div className="">We are available for teaming arrangements and subcontracting opportunities</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[180px]">Emergency Contact:</div>
                    <div className="">(918) 493-3418 - Available 24/7 for urgent facility needs</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[180px]">Email:</div>
                    <div className="">
                      <a href="mailto:inquire@williamsdiversified.com" className="text-gold hover:underline">
                        inquire@williamsdiversified.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to <span className="text-gold">Serve</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10 max-w-3xl mx-auto">
            Williams Diversified LLC is prepared to deliver mission-critical construction and facility services to federal agencies nationwide. Contact us to discuss your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/contact"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Request Capabilities Brief
            </Link>
            <a 
              href="tel:9184933418"
              className="inline-block bg-transparent border-2 border-gold hover:bg-gold hover:text-black text-gold font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Call: (918) 493-3418
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}

export default GovernmentContracting;
